
import { Component, Vue } from "vue-property-decorator";
import { mapGetters } from "vuex";
import VueI18n from "vue-i18n";
import { ProjectSectionDetails } from "@/services/projectService";

@Component({
  name: "APITab",
  computed: {
    ...mapGetters("projects", {
      currentSectionDetails: "GetCurrentSectionDetails"
    })
  }
})
export default class APITab extends Vue {
  // i18n Typing
  $t!: VueI18n["t"];

  // Vuex
  readonly currentSectionDetails!: {
    id: string;
    apiLastExport?: string;          // ISO aus Backend (z. B. "2025-12-01T00:00:00Z" oder ohne Z)
    apiLastExportAttempt?: string;   // ISO aus Backend
    apiLastExportOk?: boolean;
  };

  // UI state
  actionMessage: { class: "success" | "danger"; msg: any } | null = null;

  // Modal state
  exportModalVisible = false;
  exportDate: string | null = null; // "YYYY-MM-DD"

  /* -------------------- Computed -------------------- */
  get hasNoExportData(): boolean {
    return (
      this.currentSectionDetails.apiLastExportOk === false &&
      !this.currentSectionDetails.apiLastExport &&
      !this.currentSectionDetails.apiLastExportAttempt
    );
  }

  get exportStatusText(): string {
    return this.currentSectionDetails.apiLastExportOk
      ? (this.$t("project.section.informationModal.apiTab.ok") as string)
      : (this.$t("project.section.informationModal.apiTab.error") as string);
  }

  get lastExport() {
    return this.currentSectionDetails?.apiLastExport || null;
  }

  get lastExportAttempt() {
    return this.currentSectionDetails?.apiLastExportAttempt || null;
  }

  get lastExportOk() {
    return this.currentSectionDetails?.apiLastExportOk ?? false;
  }

  /** ===== Anzeige-Felder (OHNE UTC-Rechnung, nur Formatierung) ===== */

  // lastExport → exakt das Datum/Zeit aus dem String, nur in "DD.MM.YYYY HH:mm" formatiert
  get lastExportDisplay(): string {
    if (!this.lastExport) return "—";
    const formatted = this.formatIsoToGermanDateTime(this.lastExport);
    return formatted || this.lastExport;
  }

  // lastExportAttempt → ebenfalls nur formatiert, nichts verschoben
  get lastExportAttemptDisplay(): string {
    if (!this.lastExportAttempt) return "—";
    const formatted = this.formatIsoToGermanDateTime(this.lastExportAttempt);
    return formatted || this.lastExportAttempt;
  }

  /* -------------------- Methods -------------------- */
  openExportModal() {
    this.exportDate = null;
    this.exportModalVisible = true;
  }

  created() {
    console.log("currentSectionDetails", this.currentSectionDetails);
  }

  /**
   * Nimmt einen ISO-ähnlichen String ("YYYY-MM-DDTHH:mm:ss...") und gibt
   * "DD.MM.YYYY HH:mm" zurück – ohne irgendeine Zeitzonen-/UTC-Rechnung.
   * Wenn das Format nicht erkannt wird, kommt "" zurück.
   */
  private formatIsoToGermanDateTime(iso: string): string {
    // akzeptiert:
    // 2025-12-01T00:00:00Z
    // 2025-12-01T00:00:00+01:00
    // 2025-12-01 00:00:00
    const m = iso.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?)?/
    );
    if (!m) return "";

    const year = m[1];
    const month = m[2];
    const day = m[3];
    const hour = m[4] ?? "00";
    const minute = m[5] ?? "00";

    const datePart = `${day}.${month}.${year}`;
    const timePart = `${hour}:${minute}`;

    return `${datePart} ${timePart}`;
  }

  /** ===== Absenden: exakt gewähltes Datum + "T00:00:00" schicken ===== */
  async submitExport() {
    if (!this.exportDate) {
      this.actionMessage = { class: "danger", msg: this.$t("Bitte ein Datum wählen.") };
      return;
    }

    try {
      // Kein -1 Tag, kein UTC-Shift.
      // Wenn der User "2025-12-01" auswählt, wird "2025-12-01T00:00:00" geschickt.
      const exactIso = `${this.exportDate}T00:00:00`;

      const payload = {
        sectionId: this.currentSectionDetails.id,
        date: exactIso
      };

      const res = await ProjectSectionDetails.sendTrafficCountDataToApi(payload);

      if (res?.status === 200) {
        this.actionMessage = { class: "success", msg: this.$t("Export wurde gestartet.") };
        this.exportModalVisible = false;
      } else {
        this.actionMessage = { class: "danger", msg: this.$t("Export fehlgeschlagen.") };
      }
    } catch (err) {
      this.actionMessage = { class: "danger", msg: this.$t("Export fehlgeschlagen.") };
      this.exportModalVisible = false;
    }
  }
}
